import React, { useContext, useMemo } from 'react';

import {
	PostOfficeProvider as PostOfficeBaseProvider,
	usePostOfficeRouteUpdate,
} from '@atlassian/post-office-context';

import { useSessionData } from '@confluence/session-data';
import { RoutesContext, useRouteDataRef } from '@confluence/route-manager';
import { fg } from '@confluence/feature-gating';

import {
	sessionToPostOfficeContext,
	transformConfluenceEnvToPostOfficeEnvironment,
} from './session-to-post-office-context';
import { ConfluenceExperimentsProvider } from './confluence-experiments-context';
import { PostOfficeAnalyticsListener } from './post-office-analytics-listener';

const ConfluencePostOfficeProvider = ({ children }: { children: React.ReactNode }) => {
	const { accessStatus, activationId, cloudId, environment, locale, orgId, edition } =
		useSessionData();

	const context = useMemo(
		() => ({
			current: sessionToPostOfficeContext({
				accessStatus,
				activationId,
				cloudId,
				locale,
				orgId,
				edition,
			}),
		}),
		[activationId, cloudId, accessStatus, locale, orgId, edition],
	);

	const env = useMemo(
		() => ({ env: transformConfluenceEnvToPostOfficeEnvironment(environment) }),
		[environment],
	);

	return (
		<PostOfficeBaseProvider context={context} environment={env}>
			<PostOfficeAnalyticsListener>{children}</PostOfficeAnalyticsListener>
		</PostOfficeBaseProvider>
	);
};

export const PostOfficeProvider = ({ children }: { children: React.ReactNode }) => {
	return fg('post-office-screen-space-overlay-confluence') ? (
		<ConfluencePostOfficeProvider>
			<ConfluenceExperimentsProvider>{children}</ConfluenceExperimentsProvider>
		</ConfluencePostOfficeProvider>
	) : (
		<>{children}</>
	);
};

const ReactRouterListener = ({ children }: { children: React.ReactNode }) => {
	const routeDataRef = useRouteDataRef();

	usePostOfficeRouteUpdate([
		routeDataRef?.current?.href ?? '',
		routeDataRef.current?.routeName ?? '',
	]);

	return <>{children}</>;
};

const ConfluenceRouterListener = ({ children }: { children: React.ReactNode }) => {
	const routeContext = useContext(RoutesContext);

	usePostOfficeRouteUpdate([routeContext.getHref() ?? '', routeContext.match?.name ?? '']);

	return <>{children}</>;
};

export const PostOfficeRouterListener = ({ children }: { children: React.ReactNode }) => {
	return fg('post-office-screen-space-overlay-confluence') ? (
		fg('post-office-new-confluence-router-listener') ? (
			<ConfluenceRouterListener>{children}</ConfluenceRouterListener>
		) : (
			<ReactRouterListener>{children}</ReactRouterListener>
		)
	) : (
		<>{children}</>
	);
};
